/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: types.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the openvpn module.
────────────────────────────────────────────────────────────────*/


export type OpenVPNProtocol = 'udp' | 'tcp';
export type OpenVPNDeviceType = 'tun' | 'tap';
export type OpenVPNCompression = 'no' | 'lz4-v2' | 'lz4' | 'lzo';
export type ClientFormat = 'generic' | 'grandstream' | 'yealink' | 'fanvil';

export interface OpenVPNConfig {
    id: number;
    uuid: string;
    tenantId: number;

    // Server Settings
    enabled: boolean;
    serverAddress: string;
    serverPort: number;
    protocol: OpenVPNProtocol;
    deviceType: OpenVPNDeviceType;

    // Network Settings
    vpnSubnet: string;
    vpnNetmask: string;
    dnsServer1: string;
    dnsServer2: string;

    // Security Settings
    cipher: string;
    auth: string;
    tlsVersionMin: string;

    // Certificate Settings
    caCert?: string;
    serverCert?: string;
    serverKey?: string;
    dhParams?: string;
    taKey?: string;

    // Advanced Settings
    keepaliveInterval: number;
    keepaliveTimeout: number;
    maxClients: number;
    compress: OpenVPNCompression;

    // Audit
    createdAt?: string;
    updatedAt?: string;
}

export interface OpenVPNConfigFormState {
    // Server Settings
    enabled: boolean;
    serverAddress: string;
    serverPort: number;
    protocol: OpenVPNProtocol;
    deviceType: OpenVPNDeviceType;

    // Network Settings
    vpnSubnet: string;
    vpnNetmask: string;
    dnsServer1: string;
    dnsServer2: string;

    // Security Settings
    cipher: string;
    auth: string;
    tlsVersionMin: string;

    // Advanced Settings
    keepaliveInterval: number;
    keepaliveTimeout: number;
    maxClients: number;
    compress: OpenVPNCompression;
}

export const DEFAULT_OPENVPN_CONFIG: OpenVPNConfigFormState = {
    enabled: false,
    serverAddress: '',
    serverPort: 1194,
    protocol: 'udp',
    deviceType: 'tun',
    vpnSubnet: '10.8.0.0',
    vpnNetmask: '255.255.255.0',
    dnsServer1: '8.8.8.8',
    dnsServer2: '8.8.4.4',
    cipher: 'AES-256-GCM',
    auth: 'SHA256',
    tlsVersionMin: '1.2',
    keepaliveInterval: 10,
    keepaliveTimeout: 120,
    maxClients: 100,
    compress: 'no'
};

export interface OpenVPNClient {
    id: number;
    uuid: string;
    tenantId: number;

    // Client Info
    name: string;
    fixedIp?: string;
    format: ClientFormat;
    enabled: boolean;

    // Connection Status
    isConnected: boolean;
    connectedSince?: string;
    virtualAddress?: string;
    realAddress?: string;
    bytesReceived: number;
    bytesSent: number;
    lastSeen?: string;

    // Audit
    createdAt?: string;
    updatedAt?: string;
    revokedAt?: string;
}

export interface CreateClientRequest {
    name: string;
    fixedIp?: string;
    format: ClientFormat;
}

export interface ClientFormState {
    name: string;
    fixedIp: string;
    format: ClientFormat;
}

export const DEFAULT_CLIENT_FORM: ClientFormState = {
    name: '',
    fixedIp: '',
    format: 'generic'
};

export type TabType = 'settings' | 'clients';
